import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { useCreateMembershipTypeZoneRule } from 'src/features/membershipType/api/zoneRule/useCreateMembershipTypeZoneRule';
import { LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE } from 'src/features/membershipType/api/zoneRule/useListMembershipTypeZoneRules';
import ZoneRuleFormModal from 'src/features/membershipType/components/forms/zoneRule/ZoneRuleFormModal';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';

interface CreateZoneRuleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateZoneRuleFormModal<TData>({ className }: CreateZoneRuleFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateMembershipTypeZoneRule({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });

            toast({
                title: formatMessage(zoneRulesMessages.createZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.createZoneRuleSuccessDescription),
            });
        },
    });

    return (
        <ZoneRuleFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ parkingId, membershipTypeId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(zoneRulesMessages.zoneRule).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(zoneRulesMessages.addZoneRuleModalSubtitle)}
        />
    );
}

export default CreateZoneRuleFormModal;
